/**
 * An invoice brought in from another system (migration 0018), as plain
 * functions shared by the invoice list, the invoice page and the Inbox.
 *
 * ── ONE NUMBER, USUALLY ──
 * "Create new invoices exactly like the old references and attach the
 * original to it as source." An imported invoice KEEPS the number it was
 * issued under - the customer's copy and the GST return must say the same
 * number - and the series continues from it. The old number sits beside it
 * only when this book already had an invoice by that number and the import
 * had to take the next Fint number; then every screen says both, in one
 * form of words: "INV-0007 · originally INV-2024-017 in Zoho". When the two
 * are the same the line says where it came from: "imported from Zoho".
 *
 * No imports, so Node's test runner can pin the wording without a bundler.
 */

/** The fields any read of an imported document carries. All optional on the wire while the API lands them. */
export interface ImportedDocument {
  externalNumber?: string | null;
  sourceSystem?: string | null;
  /** The column's own tinyint until the API presents it as a boolean - `1` counts. */
  imported?: boolean | number | null;
}

/** Whether the document was recreated from another system. An external number alone is not enough: a person can type one. */
export function isImportedDocument(doc: ImportedDocument | null | undefined): boolean {
  return doc?.imported === true || doc?.imported === 1;
}

/**
 * "originally INV-2024-017 in Zoho" - the number the document had in the
 * system it came from, and where. Without a source system: "originally
 * INV-2024-017". Null when there is no external number: the line is about
 * the OTHER number, and a document without one has nothing to say.
 */
export function originallyLine(
  externalNumber: string | null | undefined,
  sourceSystem: string | null | undefined,
  ownNumber?: string | null,
): string | null {
  const number = externalNumber?.trim();
  if (!number) return null;
  const system = sourceSystem?.trim();
  // An import that KEPT its number (the usual case now) is not "originally"
  // anything - it is that number. The line says where it came from instead.
  if (ownNumber && ownNumber.trim() === number) return system ? `imported from ${system}` : null;
  return system ? `originally ${number} in ${system}` : `originally ${number}`;
}

/** The same line to start a sentence with: "Originally INV-2024-017 in Zoho". */
export function originallyTitle(
  externalNumber: string | null | undefined,
  sourceSystem: string | null | undefined,
  ownNumber?: string | null,
): string | null {
  const line = originallyLine(externalNumber, sourceSystem, ownNumber);
  return line ? line[0].toUpperCase() + line.slice(1) : null;
}

/** "INV-0007 (originally INV-2024-017)" - how a settlement names the invoice; the number alone when it has no other. */
export function invoiceRef(number: string | null | undefined, externalNumber: string | null | undefined): string {
  const own = number?.trim() || '—';
  const external = externalNumber?.trim();
  return external && external !== own ? `${own} (originally ${external})` : own;
}

/**
 * Whether a search term names this document's OTHER number.
 *
 * The list sends `q` to the server, whose search covers the number, the
 * reference and the customer. Until it covers the external number too, a
 * person typing the number they know from Zoho would get "Nothing matches
 * that" over an invoice that is right there - so the page falls back to
 * this, on the rows it has loaded (see InvoicesPage). Case-insensitive,
 * contains, as the server's `containsPattern` is.
 */
export function matchesExternalNumber(q: string, externalNumber: string | null | undefined): boolean {
  const term = q.trim().toLowerCase();
  if (!term || !externalNumber) return false;
  return externalNumber.toLowerCase().includes(term);
}

/**
 * ── ATTACHING THE ORIGINALS AFTERWARDS ──
 *
 * The import tool takes the old PDF as base64 in the same call that
 * recreates the invoice, and in practice an AI agent working from a chat
 * window cannot send it: a one-megabyte PDF is 1.3 million characters of
 * argument, and the first real migration - sixteen Zoho invoices, read
 * faithfully to the paise - arrived with no source on any of them. So the
 * originals are attached by the PERSON, in bulk, from the export folder,
 * and matched to their invoices by the number in each filename: Zoho
 * writes `Invoice_INV-000023.pdf`, Tally and most others put the number in
 * the name too.
 *
 * A number matches only as a whole token. `INV-00001` is a substring of
 * `Invoice_INV-000015.pdf`, and a match on that would put the wrong PDF on
 * the wrong invoice, silently, which is worse than no match - so the
 * character after the number must not be a letter or a digit.
 */

/** What the matcher needs of an invoice: its numbers, and whether it already has a source. */
export interface OriginalCandidate {
  id: string;
  number: string;
  externalNumber?: string | null;
  sourceFile?: { id: string } | null;
}

/** Where the match came from, or why there is none. */
export type OriginalMatchBy = 'external' | 'number' | 'none' | 'ambiguous';

export interface OriginalMatch {
  invoiceId: string | null;
  by: OriginalMatchBy;
}

/** Whether the filename names this number as a whole token: "INV-00011" in "Invoice_INV-00011.pdf", not in "INV-000115.pdf". */
export function filenameNames(filename: string, number: string | null | undefined): boolean {
  const n = number?.trim();
  if (!n) return false;
  const escaped = n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(^|[^A-Za-z0-9])${escaped}(?=$|[^A-Za-z0-9])`, 'i').test(filename);
}

/**
 * The invoice a file belongs to, by its filename. The number it had in the
 * old system first - that is the number on the PDF - then Fint's own. One
 * invoice must match, and only one: two invoices whose numbers both appear
 * is "ambiguous", and the person chooses.
 */
export function matchOriginal(filename: string, invoices: readonly OriginalCandidate[]): OriginalMatch {
  const byExternal = invoices.filter((invoice) => filenameNames(filename, invoice.externalNumber));
  if (byExternal.length === 1) return { invoiceId: byExternal[0].id, by: 'external' };
  if (byExternal.length > 1) return { invoiceId: null, by: 'ambiguous' };
  const byNumber = invoices.filter((invoice) => filenameNames(filename, invoice.number));
  if (byNumber.length === 1) return { invoiceId: byNumber[0].id, by: 'number' };
  return { invoiceId: null, by: byNumber.length > 1 ? 'ambiguous' : 'none' };
}
